import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { usePageFilters } from '@/stores/pageFiltersStore';

/**
 * useMyRoles — which halves of the Dashboard this user should see, and which they've asked for.
 *
 * A person can be a Care Support Leader, a course Facilitator, both, or (rarely) neither — an admin
 * who navigated here directly. Sections are gated on the role claim, and each data hook takes the
 * matching flag as its `enabled` prop, so a facilitator-only user never fires a leader request.
 *
 * This replaces the old ControlCenter behaviour, which called both portals for everyone and rendered
 * all tiles regardless of role (`_memory/_context/06-known-issues.md` A1).
 *
 * Note the role claim is necessary but not sufficient: a user can hold `ActsCareLeader` without a
 * `Leader` record existing yet. The data hooks use `Promise.allSettled` so that degrades to a hidden
 * section rather than an error.
 *
 * **The view toggle.** Someone who is both a leader and a facilitator gets a long page — two KPI rows,
 * four list cards, a merged schedule. In practice they are usually wearing one hat at a time, so the
 * page offers All / As Care Leader / As Facilitator and narrows to the selected half. The choice is
 * persisted per user via `usePageFilters`, because it is a working preference, not a transient filter
 * — coming back to the Dashboard should land you where you left off.
 *
 * `showLeader` / `showFacilitator` are what the page and the data hooks should consult: they fold the
 * role claim and the toggle together, so deselecting a half also stops its requests.
 */
export type DashboardView = 'all' | 'leader' | 'facilitator';

const PAGE_KEY = 'dashboard/personal';
const DEFAULTS: { view: DashboardView } = { view: 'all' };

export interface MyRoles {
  /** Holds the `ActsCareLeader` claim. */
  isLeader: boolean;
  /** Holds the `ActsCareFacilitator` claim. */
  isFacilitator: boolean;
  /** Holds both — the only case where the toggle is offered. */
  isBoth: boolean;
  /** Holds neither; the page shows a short explanation. */
  isNeither: boolean;

  /** Current toggle value. Always `'all'` for a single-role user. */
  view: DashboardView;
  setView: (view: DashboardView) => void;

  /** Render + fetch the care half? Role claim AND the toggle. */
  showLeader: boolean;
  /** Render + fetch the courses half? Role claim AND the toggle. */
  showFacilitator: boolean;
  /** Both halves are on screen at once — the page adds section dividers. */
  showBoth: boolean;
}

export function useMyRoles(): MyRoles {
  const { hasRole } = useAuth();
  const [filters, setFilter] = usePageFilters(PAGE_KEY, DEFAULTS);

  const isLeader = hasRole('ActsCareLeader');
  const isFacilitator = hasRole('ActsCareFacilitator');
  const isBoth = isLeader && isFacilitator;

  // A single-role user is pinned to 'all'. Without this, a stored 'facilitator' from when the
  // user held both roles would blank the page after the facilitator role was removed.
  const view: DashboardView = isBoth ? filters.view : 'all';

  const setView = useCallback(
    (next: DashboardView) => setFilter('view', next),
    [setFilter],
  );

  return useMemo(() => {
    const showLeader = isLeader && (view === 'all' || view === 'leader');
    const showFacilitator = isFacilitator && (view === 'all' || view === 'facilitator');

    return {
      isLeader,
      isFacilitator,
      isBoth,
      isNeither: !isLeader && !isFacilitator,
      view,
      setView,
      showLeader,
      showFacilitator,
      showBoth: showLeader && showFacilitator,
    };
  }, [isLeader, isFacilitator, isBoth, view, setView]);
}

export default useMyRoles;
